import {Component, OnInit} from '@angular/core';
import {SharedConstants} from '../../utilityClass/sharedConstants';
import {UserShortResponse} from "../../interface/userShortResponse";
import {GetPageUtil} from "../get-page-util";
import {QR_QTY_ERROR_MESSAGE} from "../../constants/promo-code.constant";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ReservedQrsService} from "../../service/reserved-qrs-service";
import {Router} from "@angular/router";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {QR_CODE_GENERATION_ERROR_MESSAGE, QR_CODE_GET_ERROR_MESSAGE} from "../../constants/global.constants";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {ReservedQrsResponse} from "../../interface/reservedQrResponse";
import {firstValueFrom, forkJoin} from "rxjs";

@Component({
  selector: 'app-admin-activity',
  templateUrl: './admin-qr-generator.component.html',
  styleUrl: './admin-qr-generator.component.scss'
})
export class AdminQrGeneratorComponent implements OnInit {

  currentPage: number = 0;
  pageSize: number = 20;

  totalRecords: number = 0;
  totalPages: number = 0;

  isLoading: boolean = false;

  users!: UserShortResponse[]
  qrCodeForm!: FormGroup;

  qrCodes: ReservedQrsResponse[] = [];

  activeCount: number = 0;
  inactiveCount: number = 0;

  readonly TAB_ACTIVE = 'active';
  readonly TAB_INACTIVE = 'inactive';

  activeTab: 'active' | 'inactive' = this.TAB_ACTIVE;

  private readonly TAB_STATUS_MAP: Record<'active' | 'inactive', string> = {
    'active': 'active',
    'inactive': 'inactive'
  };

  CONSTANT_QR_CODE_BASE64 = 'data:image/png;base64';
  DOWNLOADED_QR_NAME = 'qr-code.png';
  DOWNLOADED_PDF_NAME = 'qr-codes.pdf';

  private getPageUtil = new GetPageUtil();

  constructor(
    private router: Router,
    private qrCodeService: ReservedQrsService,
    private snackBar: SnackBarUtil) {
  }

  ngOnInit(): void {
    this.qrCodeForm = new FormGroup({
      qty: new FormControl('', [Validators.required, Validators.min(1)])
    });
    this.getAllQrCodes()
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.getAllQrCodes();
    }
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.currentPage, this.totalRecords, this.pageSize);
  }

  getFilteredQrCodes(): ReservedQrsResponse[] {
    return this.qrCodes;
  }

  getFilteredQrCodesCount(): number {
    return this.qrCodes.length;
  }

  setActiveTab(tab: 'active' | 'inactive'): void {
    this.activeTab = tab;
    this.currentPage = 0;
    this.getAllQrCodes();
  }

  generateQrCode(): void {
    if (this.qrCodeForm.invalid) {
      this.qrCodeForm.markAllAsTouched();
      return;
    }

    const qty = this.qrCodeForm.get('qty')?.value;
    this.qrCodeService.generateQrCode(qty).subscribe({
      next: () => {
        this.getAllQrCodes();
        this.qrCodeForm.reset({ qty: '' });
      },
      error: () => {
        this.showSnackbar(
          MockupDataModel.error(QR_CODE_GENERATION_ERROR_MESSAGE),
          SnackBarUtil.CLASSES.error
        );
      },
    });
  }

  getQr(qrCode: string) {
    return `${this.CONSTANT_QR_CODE_BASE64},${qrCode}`;
  }

  downloadImage(qrCodeData: string, qrUsername: string): void {
    const imageUrl = this.getQr(qrCodeData);
    const img = new Image();

    img.onload = () => {
      const TEXT_HEIGHT = 10;
      const FONT = 'bold 18px Arial';
      const FONT_COLOR = '#000';
      const BACKGROUND_COLOR = '#FFFFFF';
      const username = qrUsername?.trim() || 'QR Code';

      const qrSize = img.naturalWidth || img.width || 300;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        this.showSnackbar(
          MockupDataModel.error('Failed to create canvas context'),
          SnackBarUtil.CLASSES.error
        );
        return;
      }

      canvas.width = qrSize;
      canvas.height = qrSize + TEXT_HEIGHT + 15;

      ctx.fillStyle = BACKGROUND_COLOR;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(img, 0, 0, qrSize, qrSize);

      ctx.fillStyle = FONT_COLOR;
      ctx.font = FONT;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(username, canvas.width - 10, canvas.height - 10);

      if (canvas.toBlob) {
        canvas.toBlob((blob) => {
          if (blob) {
            this.downloadBlob(blob);
          } else {
            this.downloadDataUrl(canvas.toDataURL('image/png'));
          }
        }, 'image/png');
      } else {
        this.downloadDataUrl(canvas.toDataURL('image/png'));
      }
    };

    img.onerror = () => {
      this.showSnackbar(
        MockupDataModel.error('Failed to load QR code image'),
        SnackBarUtil.CLASSES.error
      );
      this.downloadFallback(imageUrl);
    };
    img.src = imageUrl;
  }

  private downloadBlob(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    try {
      link.href = url;
      link.download = this.DOWNLOADED_QR_NAME;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      this.showSnackbar(
        MockupDataModel.error('Failed to download QR code'),
        SnackBarUtil.CLASSES.error
      );
    } finally {
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }
  }

  private downloadDataUrl(dataUrl: string): void {
    const link = document.createElement('a');
    try {
      link.href = dataUrl;
      link.download = this.DOWNLOADED_QR_NAME;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      this.showSnackbar(
        MockupDataModel.error('Failed to download QR code'),
        SnackBarUtil.CLASSES.error
      );
    } finally {
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    }
  }

  private downloadFallback(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = this.DOWNLOADED_QR_NAME;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  private getAllQrCodes() {
    this.isLoading = true;
    const status = this.TAB_STATUS_MAP[this.activeTab];
    this.qrCodeService.getAllQrCodes(this.currentPage, this.pageSize, status).subscribe({
      next: (response) => {
        this.qrCodes = response.content;
        this.totalRecords = response.totalElements;
        this.totalPages = response.totalPages;
        this.isLoading = false;
      },
      error: () => {
        this.showSnackbar(MockupDataModel.error(QR_CODE_GET_ERROR_MESSAGE ), SnackBarUtil.CLASSES.error);
        this.isLoading = false;
      }
    });
    this.loadCounts();
  }

  private loadCounts(): void {
    forkJoin({
      active: this.qrCodeService.getAllQrCodes(0, 1, this.TAB_STATUS_MAP['active']),
      inactive: this.qrCodeService.getAllQrCodes(0, 1, this.TAB_STATUS_MAP['inactive'])
    }).subscribe({
      next: ({ active, inactive }) => {
        this.activeCount = active.totalElements;
        this.inactiveCount = inactive.totalElements;
      }
    });
  }

  goToUserCard(qrUsername?: string | null) {
    const route = qrUsername ? `/${qrUsername}` : '/error-page';
    this.router.navigate([route]);
  }

  async downloadAllQRCodesAsPDF(): Promise<void> {
    this.isLoading = true;
    try {
      const currentPageQrCodes = this.getFilteredQrCodes();
      if (!currentPageQrCodes.length) {
        this.showSnackbar(MockupDataModel.error('No QR codes to download'), SnackBarUtil.CLASSES.error);
        return;
      }

      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');

      await this.generatePDF(pdf, currentPageQrCodes);
      pdf.save(this.DOWNLOADED_PDF_NAME);

      this.showSnackbar(MockupDataModel.success('PDF successfully downloaded'), SnackBarUtil.CLASSES.success);
    } catch {
      this.showSnackbar(MockupDataModel.error('Error generating PDF'), SnackBarUtil.CLASSES.error);
    } finally {
      this.isLoading = false;
    }
  }

  private async generatePDF(pdf: any, qrCodes: ReservedQrsResponse[]): Promise<void> {
    const margin = 10;
    const qrSize = 40;
    const spacing = 5;
    const colsPerRow = 4;
    const rowsPerPage = 5;
    const qrCodesPerPage = colsPerRow * rowsPerPage;
    const batchSize = 20;

    let processed = 0;
    let x = margin, y = margin;

    for (let i = 0; i < qrCodes.length; i += batchSize) {
      const batch = qrCodes.slice(i, i + batchSize);
      const images = await this.renderBatch(batch);

      for (const img of images) {
        if (!img) continue;

        if (processed > 0 && processed % qrCodesPerPage === 0) {
          pdf.addPage();
          x = margin;
          y = margin;
        } else if (processed > 0 && processed % colsPerRow === 0) {
          x = margin;
          y += qrSize + spacing;
        }

        pdf.addImage(img, 'PNG', x, y, qrSize, qrSize);
        x += qrSize + spacing;
        processed++;
      }
    }
  }

  private async renderBatch(batch: ReservedQrsResponse[]): Promise<(string | null)[]> {
    return Promise.all(
      batch.map(async qr => {
        const imageUrl = this.getQr(qr.qrCode);
        const username = qr.qrUsername?.trim() || 'QR Code';
        try {
          return await this.renderQRCode(imageUrl, username);
        } catch {
          return null;
        }
      })
    );
  }

  private renderQRCode(imageUrl: string, username: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const textHeight = 15;
        const font = '18px Arial';
        const fontColor = '#000';
        const background = '#FFFFFF';
        const qrSize = img.naturalWidth || img.width || 300;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('Canvas context not available');

        canvas.width = qrSize;
        canvas.height = qrSize + textHeight + 10;

        ctx.fillStyle = background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(img, 0, 0, qrSize, qrSize);

        ctx.fillStyle = fontColor;
        ctx.font = font;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(username, canvas.width / 2, canvas.height - 5);

        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = () => reject('Failed to load QR image');
      img.src = imageUrl;
    });
  }

  protected readonly SharedConstants = SharedConstants;
  protected readonly QTY_ERROR_MESSAGE = QR_QTY_ERROR_MESSAGE;
}
